import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Package, DollarSign, TrendingUp, TrendingDown, Percent, Target, Shield, PiggyBank, ChevronRight, Wallet, ArrowDownCircle, ArrowUpCircle, Banknote, Receipt } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import TrialBanner from '../components/TrialBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [liquidity, setLiquidity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPackageFilter, setSelectedPackageFilter] = useState('all');
  
  // Package detail modal state
  const [showPackageDetail, setShowPackageDetail] = useState(false);
  const [selectedPackageDetail, setSelectedPackageDetail] = useState(null);
  const [packageBookings, setPackageBookings] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedPackageFilter]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = selectedPackageFilter !== 'all' ? { package_id: selectedPackageFilter } : {};
      
      const [statsRes, packagesRes, bookingsRes, expensesRes, liquidityRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers, params }),
        axios.get(`${API}/packages`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/expenses`, { headers }),
        axios.get(`${API}/dashboard/liquidity`, { headers })
      ]);
      
      setStats(statsRes.data);
      setPackages(packagesRes.data);
      setBookings(bookingsRes.data);
      setExpenses(expensesRes.data);
      setLiquidity(liquidityRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle package click to show details
  const handlePackageClick = (packageDetail) => {
    const fullPackage = packages.find(p => p.package_id === packageDetail.package_id);
    setSelectedPackageDetail({ ...packageDetail, ...fullPackage });
    
    // Get bookings for this package
    const pkgBookings = bookings.filter(b => b.package_id === packageDetail.package_id);
    setPackageBookings(pkgBookings);
    
    setShowPackageDetail(true);
  };

  // Map cost elements to bookings for the unified table
  const getMappedCostElementsAndBookings = () => {
    if (!selectedPackageDetail) return [];
    
    const costElements = selectedPackageDetail.cost_elements || [];
    const mappedData = [];
    
    // Filter out Contingency and Profit from cost elements
    const operationalElements = costElements.filter(
      elem => !['Contingency', 'Profit'].includes(elem.expense_type)
    );
    
    operationalElements.forEach(elem => {
      // Find bookings linked to this cost element
      const linkedBookings = packageBookings.filter(b => b.cost_element_id === elem.element_id);
      
      if (linkedBookings.length > 0) {
        // Add a row for each booking
        linkedBookings.forEach((booking, idx) => {
          mappedData.push({
            type: 'mapped',
            elementType: elem.expense_type,
            elementId: elem.element_id,
            plannedProvider: elem.service_provider || '-',
            location: elem.location || '-',
            estimatedValue: idx === 0 ? elem.value : null, // Only show on first row
            bookingId: booking.booking_id,
            bookedProvider: booking.service_provider,
            actualValue: booking.booking_cost || booking.actual_cost || booking.service_value,
            paymentStatus: booking.payment_status,
            isFirstOfGroup: idx === 0,
            groupSize: linkedBookings.length
          });
        });
      } else {
        // No booking for this element
        mappedData.push({
          type: 'unbooked',
          elementType: elem.expense_type,
          elementId: elem.element_id,
          plannedProvider: elem.service_provider || '-',
          location: elem.location || '-',
          estimatedValue: elem.value,
          bookingId: null,
          bookedProvider: '-',
          actualValue: 0,
          paymentStatus: '-',
          isFirstOfGroup: true,
          groupSize: 1
        });
      }
    });
    
    // Add unlinked bookings (bookings without cost_element_id)
    const unlinkedBookings = packageBookings.filter(b => !b.cost_element_id);
    unlinkedBookings.forEach(booking => {
      mappedData.push({
        type: 'unlinked',
        elementType: 'Unlinked Booking',
        elementId: '-',
        plannedProvider: '-',
        location: '-',
        estimatedValue: 0,
        bookingId: booking.booking_id,
        bookedProvider: booking.service_provider,
        actualValue: booking.booking_cost || booking.actual_cost || booking.service_value,
        paymentStatus: booking.payment_status,
        isFirstOfGroup: true,
        groupSize: 1
      });
    });
    
    return mappedData;
  };

  if (loading) {
    return <div className="text-muted-foreground">{t('common.loading')}</div>;
  }

  // KPI values from stats
  const totalSoldValue = stats?.total_sold_value || 0;
  const estimatedCost = stats?.estimated_cost || 0;
  const actualCost = stats?.actual_cost || 0;
  const otherExpenses = stats?.other_expenses || 0;
  const plannedProfit = stats?.planned_profit || 0;
  const estimatedProfit = stats?.estimated_profit || 0;  // Renamed from actualProfit
  const plannedContingency = stats?.planned_contingency || 0;
  const usedContingency = stats?.used_contingency || 0;
  const remainingContingency = stats?.remaining_contingency || 0;
  const margin = stats?.margin || 0;
  const marginPercent = stats?.margin_percent || 0;

  const mainKPIs = [
    { 
      title: t('dashboard.totalSoldValue'), 
      value: `€${totalSoldValue.toLocaleString()}`, 
      icon: DollarSign, 
      color: 'text-green-600', 
      bgColor: 'bg-green-500/10', 
      subtitle: t('dashboard.totalSoldValueDesc') 
    },
    { 
      title: t('dashboard.estimatedCost'), 
      value: `€${estimatedCost.toLocaleString()}`, 
      icon: Target, 
      color: 'text-blue-500', 
      bgColor: 'bg-blue-500/10', 
      subtitle: t('dashboard.estimatedCostDesc') 
    },
    { 
      title: t('dashboard.actualCost'), 
      value: `€${actualCost.toLocaleString()}`, 
      icon: TrendingDown, 
      color: 'text-orange-500', 
      bgColor: 'bg-orange-500/10', 
      subtitle: t('dashboard.actualCostDesc') 
    },
    { 
      title: t('dashboard.margin'), 
      value: `€${margin.toLocaleString()}`, 
      subtitle: `${marginPercent}% ${t('dashboard.marginDesc')}`, 
      icon: Percent, 
      color: margin >= 0 ? 'text-green-600' : 'text-red-500', 
      bgColor: margin >= 0 ? 'bg-green-500/10' : 'bg-red-500/10' 
    },
  ];

  const profitKPIs = [
    { 
      title: t('dashboard.plannedProfit'), 
      value: `€${plannedProfit.toLocaleString()}`, 
      icon: PiggyBank, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-500/10',
      description: t('dashboard.plannedProfitDesc')
    },
    { 
      title: t('dashboard.estimatedProfit'), 
      value: `€${estimatedProfit.toLocaleString()}`, 
      icon: TrendingUp, 
      color: 'text-green-600', 
      bgColor: 'bg-green-500/10',
      description: t('dashboard.estimatedProfitDesc')
    },
    { 
      title: t('dashboard.plannedContingency'), 
      value: `€${plannedContingency.toLocaleString()}`, 
      icon: Shield, 
      color: 'text-blue-500', 
      bgColor: 'bg-blue-500/10',
      description: t('dashboard.plannedContingencyDesc')
    },
    { 
      title: t('dashboard.usedContingency'), 
      value: `€${usedContingency.toLocaleString()}`, 
      icon: Shield, 
      color: 'text-orange-500', 
      bgColor: 'bg-orange-500/10',
      description: t('dashboard.usedContingencyDesc')
    },
    { 
      title: t('dashboard.remainingContingency'), 
      value: `€${remainingContingency.toLocaleString()}`, 
      icon: Shield, 
      color: remainingContingency >= 0 ? 'text-green-500' : 'text-red-500', 
      bgColor: remainingContingency >= 0 ? 'bg-green-500/10' : 'bg-red-500/10',
      description: t('dashboard.remainingContingencyDesc')
    },
  ];

  const costVsRevenueData = [
    { name: 'Revenue', value: totalSoldValue, fill: '#22c55e' },
    { name: t('dashboard.estimatedCost'), value: estimatedCost, fill: '#3b82f6' },
    { name: t('dashboard.actualCost'), value: actualCost, fill: '#f97316' },
    { name: t('dashboard.otherExpenses'), value: otherExpenses, fill: '#ef4444' },
  ];

  const profitContingencyData = [
    { name: t('dashboard.plannedProfit'), value: plannedProfit, fill: '#a855f7' },
    { name: t('dashboard.estimatedProfit'), value: estimatedProfit, fill: '#22c55e' },
    { name: t('dashboard.usedContingency'), value: usedContingency, fill: '#f97316' },
    { name: t('dashboard.remainingContingency'), value: remainingContingency, fill: '#3b82f6' },
  ];

  // Calculate totals for the mapped table
  const mappedData = showPackageDetail ? getMappedCostElementsAndBookings() : [];
  const totalEstimated = mappedData.reduce((sum, row) => sum + (row.estimatedValue || 0), 0);
  const totalActual = mappedData.reduce((sum, row) => sum + (row.actualValue || 0), 0);
  const totalVariance = totalEstimated - totalActual;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      <TrialBanner />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold text-foreground tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline">{t('dashboard.filterByPackage')}:</span>
          <Select value={selectedPackageFilter} onValueChange={setSelectedPackageFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder={t('dashboard.allPackages')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('dashboard.allPackages')}</SelectItem>
              {packages.map(pkg => (
                <SelectItem key={pkg.package_id} value={pkg.package_id}>
                  {pkg.package_id} - {pkg.client_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {mainKPIs.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.title} className="bg-card border border-border/60 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
                <div className={`p-1.5 sm:p-2 rounded-lg ${card.bgColor}`}>
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${card.color}`} />
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <div className={`text-lg sm:text-2xl font-heading font-bold ${card.color}`}>{card.value}</div>
                {card.subtitle && <p className="text-xs text-muted-foreground mt-1 truncate">{card.subtitle}</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Profit & Contingency Panel */}
      <Card className="border-2 border-primary/20">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-xl">
            <PiggyBank className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            {t('dashboard.profitContingencyMonitoring')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {profitKPIs.map((kpi) => {
              const Icon = kpi.icon;
              return (
                <div key={kpi.title} className={`p-3 sm:p-4 rounded-lg ${kpi.bgColor}`}>
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <Icon className={`w-3 h-3 sm:w-4 sm:h-4 ${kpi.color}`} />
                    <span className="text-xs font-medium text-muted-foreground truncate">{kpi.title}</span>
                  </div>
                  <div className={`text-base sm:text-xl font-bold ${kpi.color}`}>{kpi.value}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Liquidity Monitoring Panel */}
      {liquidity && (
        <Card className="border-2 border-green-500/20">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-xl">
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              {t('dashboard.liquidityMonitoring')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {/* Row 1: Receivables (Customer Side) */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">{t('dashboard.receivables')}</h4>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {/* Total Receivable */}
                <div className="p-3 sm:p-4 rounded-lg bg-blue-50">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <Receipt className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                    <span className="text-xs font-medium text-muted-foreground truncate">{t('dashboard.totalReceivable')}</span>
                  </div>
                  <div className="text-base sm:text-xl font-bold text-blue-600">
                    €{(liquidity.total_receivable || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{t('dashboard.totalReceivableDesc')}</p>
                </div>
                
                {/* Total Received */}
                <div className="p-3 sm:p-4 rounded-lg bg-green-50">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <ArrowDownCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                    <span className="text-xs font-medium text-muted-foreground truncate">{t('dashboard.totalReceived')}</span>
                  </div>
                  <div className="text-base sm:text-xl font-bold text-green-600">
                    €{(liquidity.total_received || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{t('dashboard.totalReceivedDesc')}</p>
                </div>
                
                {/* Net Receivable */}
                <div className="p-3 sm:p-4 rounded-lg bg-purple-50">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <Receipt className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                    <span className="text-xs font-medium text-muted-foreground truncate">{t('dashboard.netReceivable')}</span>
                  </div>
                  <div className="text-base sm:text-xl font-bold text-purple-600">
                    €{(liquidity.net_receivable || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{t('dashboard.netReceivableDesc')}</p>
                </div>
              </div>
            </div>
            
            {/* Row 2: Payables (Supplier Side) */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">{t('dashboard.payables')}</h4>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {/* Total Payable */}
                <div className="p-3 sm:p-4 rounded-lg bg-orange-50">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <Banknote className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
                    <span className="text-xs font-medium text-muted-foreground truncate">{t('dashboard.totalPayable')}</span>
                  </div>
                  <div className="text-base sm:text-xl font-bold text-orange-600">
                    €{(liquidity.total_payable || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{t('dashboard.totalPayableDesc')}</p>
                </div>
                
                {/* Total Paid */}
                <div className="p-3 sm:p-4 rounded-lg bg-red-50">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <ArrowUpCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
                    <span className="text-xs font-medium text-muted-foreground truncate">{t('dashboard.totalPaid')}</span>
                  </div>
                  <div className="text-base sm:text-xl font-bold text-red-600">
                    €{(liquidity.total_paid || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{t('dashboard.totalPaidDesc')}</p>
                </div>
                
                {/* Net Payable */}
                <div className="p-3 sm:p-4 rounded-lg bg-amber-50">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <Banknote className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
                    <span className="text-xs font-medium text-muted-foreground truncate">{t('dashboard.netPayable')}</span>
                  </div>
                  <div className="text-base sm:text-xl font-bold text-amber-600">
                    €{(liquidity.net_payable || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{t('dashboard.netPayableDesc')}</p>
                </div>
              </div>
            </div>
            
            {/* Net Cash Position - Full Width */}
            <div className={`p-3 sm:p-4 rounded-lg ${liquidity.net_cash_position >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Wallet className={`w-4 h-4 sm:w-5 sm:h-5 ${liquidity.net_cash_position >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                  <span className="text-sm font-medium">{t('dashboard.netCashPosition')}</span>
                </div>
                <div className={`text-xl sm:text-2xl font-bold ${liquidity.net_cash_position >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  €{(liquidity.net_cash_position || 0).toLocaleString()}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{t('dashboard.netCashPositionDesc')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.totalPackages')}</CardTitle>
            <Package className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-bold">{stats?.total_packages || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.totalBookings')}</CardTitle>
            <Package className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-bold">{stats?.total_bookings || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.pendingPayments')}</CardTitle>
            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-bold">{stats?.pending_payments || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.otherExpenses')}</CardTitle>
            <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-bold text-red-500">€{otherExpenses.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg font-heading font-semibold">{t('dashboard.costVsRevenue')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={costVsRevenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => `€${value.toLocaleString()}`} />
                <Bar dataKey="value" name="Amount">
                  {costVsRevenueData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg font-heading font-semibold">{t('dashboard.profitBreakdown')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={profitContingencyData.filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={90}
                  dataKey="value"
                >
                  {profitContingencyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `€${value.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Package Details Table - Clickable */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading font-semibold">{t('dashboard.packageDetails')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('dashboard.clickToViewDetails')}</p>
        </CardHeader>
        <CardContent>
          {stats?.package_details && stats.package_details.length > 0 ? (
            <div className="w-full overflow-auto rounded-lg border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead>{t('packages.packageId')}</TableHead>
                    <TableHead>{t('packages.clientName')}</TableHead>
                    <TableHead>{t('packages.packageType')}</TableHead>
                    <TableHead className="text-right">{t('packages.saleValue')}</TableHead>
                    <TableHead className="text-right">{t('packages.estimatedCost')}</TableHead>
                    <TableHead className="text-right">{t('packages.actualCost')}</TableHead>
                    <TableHead className="text-right">{t('packages.contingency')}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.package_details.map((pkg) => {
                    return (
                      <TableRow 
                        key={pkg.package_id} 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handlePackageClick(pkg)}
                      >
                        <TableCell>
                          <Badge variant={pkg.status === 'completed' ? 'default' : pkg.status === 'active' ? 'secondary' : 'outline'}>
                            {pkg.status || 'active'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{pkg.package_id}</TableCell>
                        <TableCell>{pkg.client_name}</TableCell>
                        <TableCell>
                          <Badge variant={pkg.use_bottom_up ? 'default' : 'secondary'} className="text-xs">
                            {pkg.use_bottom_up ? t('packages.bottomUp') : t('packages.topDown')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          {pkg.currency} {pkg.sale_value.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-blue-600">
                          {pkg.currency} {pkg.planned_cost.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-orange-600">
                          {pkg.currency} {pkg.actual_cost.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {pkg.use_bottom_up ? (
                            <div>
                              <span className="text-muted-foreground">{t('dashboard.used')}: </span>
                              <span className="text-orange-500">{pkg.currency} {pkg.used_contingency?.toLocaleString()}</span>
                              <br />
                              <span className="text-muted-foreground">{t('dashboard.remaining')}: </span>
                              <span className={pkg.remaining_contingency >= 0 ? 'text-green-500' : 'text-red-500'}>
                                {pkg.currency} {pkg.remaining_contingency?.toLocaleString()}
                              </span>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('packages.noPackages')}</p>
              <p className="text-sm mt-2">{t('packages.createFirst')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Package Detail Modal - EXPANDED with Mapped Table */}
      <Dialog open={showPackageDetail} onOpenChange={setShowPackageDetail}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Package className="w-5 h-5" />
              {t('packageModal.title')}: {selectedPackageDetail?.package_id}
            </DialogTitle>
            <DialogDescription>
              {selectedPackageDetail?.client_name} - {t('packageModal.costBreakdown')}
            </DialogDescription>
          </DialogHeader>

          {selectedPackageDetail && (
            <div className="space-y-6 py-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-600 font-medium">{t('packages.saleValue')}</p>
                  <p className="text-xl font-bold text-green-700">
                    {selectedPackageDetail.currency} {selectedPackageDetail.sale_value?.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium">{t('packages.estimatedCost')}</p>
                  <p className="text-xl font-bold text-blue-700">
                    {selectedPackageDetail.currency} {selectedPackageDetail.planned_cost?.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-xs text-orange-600 font-medium">{t('packages.actualCost')}</p>
                  <p className="text-xl font-bold text-orange-700">
                    {selectedPackageDetail.currency} {selectedPackageDetail.actual_cost?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Profit & Contingency */}
              {selectedPackageDetail.use_bottom_up && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-600">{t('dashboard.plannedProfit')}</p>
                    <p className="font-semibold text-purple-700">{selectedPackageDetail.currency} {selectedPackageDetail.planned_profit?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">{t('dashboard.plannedContingency')}</p>
                    <p className="font-semibold text-gray-700">{selectedPackageDetail.currency} {selectedPackageDetail.planned_contingency?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-600">{t('dashboard.usedContingency')}</p>
                    <p className="font-semibold text-orange-700">{selectedPackageDetail.currency} {selectedPackageDetail.used_contingency?.toLocaleString()}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${selectedPackageDetail.remaining_contingency >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className={`text-xs ${selectedPackageDetail.remaining_contingency >= 0 ? 'text-green-600' : 'text-red-600'}`}>{t('dashboard.remainingContingency')}</p>
                    <p className={`font-semibold ${selectedPackageDetail.remaining_contingency >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {selectedPackageDetail.currency} {selectedPackageDetail.remaining_contingency?.toLocaleString()}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${selectedPackageDetail.variance >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
                    <p className={`text-xs ${selectedPackageDetail.variance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{t('packageModal.variance')}</p>
                    <p className={`font-semibold ${selectedPackageDetail.variance >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                      {selectedPackageDetail.currency} {selectedPackageDetail.variance?.toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {/* Package Cash Flow Section */}
              <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50/30">
                <h3 className="font-semibold mb-3 text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-emerald-600" />
                  {t('packageModal.cashFlow')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-green-600 font-medium">{t('packageModal.receivedFromCustomer')}</p>
                    <p className="text-lg font-bold text-green-700">
                      {selectedPackageDetail.currency} {(selectedPackageDetail.amount_received || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-red-600 font-medium">{t('packageModal.paidToSuppliers')}</p>
                    <p className="text-lg font-bold text-red-700">
                      {selectedPackageDetail.currency} {(selectedPackageDetail.paid_amount || packageBookings.reduce((sum, b) => sum + (b.supplier_amount_paid || 0), 0)).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-blue-600 font-medium">{t('packageModal.receivableFromCustomer')}</p>
                    <p className="text-lg font-bold text-blue-700">
                      {selectedPackageDetail.currency} {(selectedPackageDetail.sale_value - (selectedPackageDetail.amount_received || 0)).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-orange-600 font-medium">{t('packageModal.payableToSuppliers')}</p>
                    <p className="text-lg font-bold text-orange-700">
                      {selectedPackageDetail.currency} {(selectedPackageDetail.payable_to_suppliers || packageBookings.reduce((sum, b) => sum + (b.supplier_balance_due || 0), 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
                {/* Net Cash Position for this package */}
                {(() => {
                  const received = selectedPackageDetail.amount_received || 0;
                  const paid = selectedPackageDetail.paid_amount || packageBookings.reduce((sum, b) => sum + (b.supplier_amount_paid || 0), 0);
                  const netCash = received - paid;
                  return (
                    <div className={`mt-3 p-3 rounded-lg ${netCash >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">{t('packageModal.netCashPosition')}</span>
                        <span className={`text-xl font-bold ${netCash >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {selectedPackageDetail.currency} {netCash.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* UNIFIED Cost Elements & Bookings Mapping Table */}
              <div>
                <h3 className="font-semibold mb-3 text-lg">{t('packageModal.costElementsAndBookings')}</h3>
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="bg-blue-50 text-blue-700 font-semibold" colSpan={4}>
                          {t('packages.costElements')} ({t('packageModal.salesPlanning')})
                        </TableHead>
                        <TableHead className="bg-orange-50 text-orange-700 font-semibold" colSpan={4}>
                          {t('bookings.title')} ({t('packageModal.actualCosts')})
                        </TableHead>
                      </TableRow>
                      <TableRow className="bg-muted/30">
                        <TableHead className="bg-blue-50/50">{t('packageModal.elementType')}</TableHead>
                        <TableHead className="bg-blue-50/50">{t('packageModal.plannedProvider')}</TableHead>
                        <TableHead className="bg-blue-50/50">{t('packageModal.location')}</TableHead>
                        <TableHead className="bg-blue-50/50 text-right">{t('packageModal.estimatedValue')}</TableHead>
                        <TableHead className="bg-orange-50/50">{t('bookings.bookingId')}</TableHead>
                        <TableHead className="bg-orange-50/50">{t('packageModal.bookedProvider')}</TableHead>
                        <TableHead className="bg-orange-50/50 text-right">{t('packageModal.actualValue')}</TableHead>
                        <TableHead className="bg-orange-50/50 text-right">{t('packageModal.variance')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mappedData.length > 0 ? mappedData.map((row, idx) => (
                        <TableRow key={idx} className={row.type === 'unbooked' ? 'bg-yellow-50/30' : row.type === 'unlinked' ? 'bg-gray-50/50' : ''}>
                          <TableCell className="bg-blue-50/20">
                            {row.isFirstOfGroup && (
                              <Badge variant="outline">{row.elementType}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="bg-blue-50/20">{row.isFirstOfGroup ? row.plannedProvider : ''}</TableCell>
                          <TableCell className="bg-blue-50/20">{row.isFirstOfGroup ? row.location : ''}</TableCell>
                          <TableCell className="bg-blue-50/20 text-right font-mono text-blue-600">
                            {row.estimatedValue !== null && row.estimatedValue > 0 ? `${selectedPackageDetail.currency} ${row.estimatedValue.toLocaleString()}` : '-'}
                          </TableCell>
                          <TableCell className="bg-orange-50/20 font-mono text-xs">
                            {row.bookingId || <span className="text-muted-foreground italic">{t('packageModal.noBooking')}</span>}
                          </TableCell>
                          <TableCell className="bg-orange-50/20">{row.bookedProvider}</TableCell>
                          <TableCell className="bg-orange-50/20 text-right font-mono text-orange-600 font-semibold">
                            {row.actualValue > 0 ? `${selectedPackageDetail.currency} ${row.actualValue.toLocaleString()}` : '-'}
                          </TableCell>
                          <TableCell className="bg-orange-50/20 text-right font-mono">
                            {row.isFirstOfGroup && row.estimatedValue !== null && row.estimatedValue > 0 ? (
                              <span className={row.estimatedValue - row.actualValue >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {selectedPackageDetail.currency} {(row.estimatedValue - row.actualValue).toLocaleString()}
                              </span>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            {t('packages.noCostElements')}
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Totals Row */}
                      {mappedData.length > 0 && (
                        <TableRow className="bg-muted/50 font-semibold border-t-2">
                          <TableCell colSpan={3} className="text-right">{t('packageModal.totalEstimated')}:</TableCell>
                          <TableCell className="text-right font-mono text-blue-700">
                            {selectedPackageDetail.currency} {totalEstimated.toLocaleString()}
                          </TableCell>
                          <TableCell colSpan={2} className="text-right">{t('packageModal.totalActual')}:</TableCell>
                          <TableCell className="text-right font-mono text-orange-700">
                            {selectedPackageDetail.currency} {totalActual.toLocaleString()}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${totalVariance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {selectedPackageDetail.currency} {totalVariance.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Package Summary */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h3 className="font-semibold mb-3">{t('packageModal.summary')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('packageModal.packageType')}:</p>
                    <p className="font-medium">{selectedPackageDetail.use_bottom_up ? t('packages.bottomUp') : t('packages.topDown')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('common.status')}:</p>
                    <Badge variant={selectedPackageDetail.status === 'completed' ? 'default' : 'secondary'}>
                      {selectedPackageDetail.status || 'active'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('packageModal.totalBookings')}:</p>
                    <p className="font-medium">{packageBookings.length}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('packageModal.paidBookings')}:</p>
                    <p className="font-medium">{packageBookings.filter(b => b.payment_status === 'Paid').length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
